import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Settings,
  DollarSign,
  Users,
  BarChart3,
  ArrowLeft,
  Edit2,
  Check,
  X,
  Wallet,
  Server,
  Database,
  Menu,
  Home,
  ChevronRight,
  TrendingUp,
  Activity,
} from "lucide-react";

import Input from "../../../components/input";
import Spinner from "../../../components/spinner";
import { usePartner } from "../../../contexts/PartnerContext";
import { ThemeContext } from "../../../components/themeContext";
import LogoIcon from "../../../assets/images/cloudVantage Logo.png";
import WhiteLogoIcon from "../../../assets/images/cloudVantage Logo White.png";
import { capitalizeFirstLetter } from "../../../hooks/constants";
import {
  useListPartnerDews,
  useListPartnerPriceLists,
  useListPartnerResources,
  useListPartnerWallet,
} from "./resuableEffect";
import { setPartnerFlavorPrices } from "../../../hooks/local/userReducer";

// Validation schema for price updates
const PRICE_VALIDATION_SCHEMA = Yup.object({
  newPrice: Yup.number()
    .required("Price is required")
    .min(0, "Price must be positive"),
});

// Navigation items configuration
const NAVIGATION_ITEMS = [
  { id: "dashboard", label: "Dashboard", icon: Home },
  { id: "resources", label: "Resources", icon: Server },
  { id: "pricing", label: "Flavor Pricing", icon: DollarSign },
  { id: "wallet", label: "Wallet", icon: Wallet },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "settings", label: "Settings", icon: Settings },
];

const PartnerAdminLayout = () => {
  // Set document title
  document.title = "Partner View - CloudVantage";

  // Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const {
    partnerInfo,
    currentSubdomain,
    loading: partnerLoading,
    switchToCustomerView,
    isWhiteLabel,
    isPartnerAdmin,
  } = usePartner();
  const { loading: userLoading } = useSelector((state) => state.user);

  // State
  const [activeTab, setActiveTab] = useState("dashboard");
  const [editingResource, setEditingResource] = useState(null);
  const [editingFlavor, setEditingFlavor] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Check if partner data should be loaded
  const shouldLoadPartnerData = Boolean(
    partnerInfo && currentSubdomain && (isWhiteLabel || isPartnerAdmin)
  );

  // Resource hooks (moved to top level to comply with Rules of Hooks)
  const partnerResourcesServer = useListPartnerResources(
    shouldLoadPartnerData ? `?partner_domain=${currentSubdomain}&resource_type=server` : null
  );
  const partnerResourcesVolume = useListPartnerResources(
    shouldLoadPartnerData ? `?partner_domain=${currentSubdomain}&resource_type=volume` : null
  );
  const partnerResourcesCluster = useListPartnerResources(
    shouldLoadPartnerData ? `?partner_domain=${currentSubdomain}&resource_type=cluster` : null
  );
  const partnerResourcesFloatingIp = useListPartnerResources(
    shouldLoadPartnerData ? `?partner_domain=${currentSubdomain}&resource_type=floating_ip` : null
  );
  const partnerResourcesInterface = useListPartnerResources(
    shouldLoadPartnerData ? `?partner_domain=${currentSubdomain}&resource_type=interface` : null
  );
  const partnerResourcesPoolMember = useListPartnerResources(
    shouldLoadPartnerData ? `?partner_domain=${currentSubdomain}&resource_type=pool_member` : null
  );
  const partnerResourcesBackup = useListPartnerResources(
    shouldLoadPartnerData ? `?partner_domain=${currentSubdomain}&resource_type=backup` : null
  );
  const partnerResourcesServerSnapshot = useListPartnerResources(
    shouldLoadPartnerData ? `?partner_domain=${currentSubdomain}&resource_type=server_snapshot` : null
  );
  const partnerResourcesVolumeSnapshot = useListPartnerResources(
    shouldLoadPartnerData ? `?partner_domain=${currentSubdomain}&resource_type=volume_snapshot` : null
  );

  const partnerPricing = useListPartnerPriceLists(
    shouldLoadPartnerData ? `?partner_domain=${currentSubdomain}` : null
  );

  const partnerWallet = useListPartnerWallet(
    shouldLoadPartnerData ? `?partner_domain=${currentSubdomain}` : null
  );

  const partnerFlavors = useListPartnerDews(
    shouldLoadPartnerData ? `?partner_domain=${currentSubdomain}` : null
  );

  // Mock data state
  const [mockData, setMockData] = useState({
    resources: [],
    flavors: [],
    wallet: {
      balance: 0,
      currency: "NGN",
      lastUpdated: new Date().toISOString().split("T")[0],
    },
    analytics: {
      totalRevenue: 0,
      activeCustomers: 0,
      activeResources: 0,
    },
  });

  // Update mock data when API data is available
  useEffect(() => {
    if (partnerFlavors?.flavor && partnerPricing?.data?.[0] && partnerWallet?.wallet) {
      try {
        const pricingData = partnerPricing.data[0];

        // Combine all resources
        const allResources = [
          ...(partnerResourcesServer?.data || []),
          ...(partnerResourcesVolume?.data || []),
          ...(partnerResourcesCluster?.data || []),
          ...(partnerResourcesFloatingIp?.data || []),
          ...(partnerResourcesInterface?.data || []),
          ...(partnerResourcesPoolMember?.data || []),
          ...(partnerResourcesBackup?.data || []),
          ...(partnerResourcesServerSnapshot?.data || []),
          ...(partnerResourcesVolumeSnapshot?.data || []),
        ].filter(Boolean);

        // Map flavors
        const mappedFlavors = partnerFlavors.flavor
          .filter((f) => f.show_to_frontend)
          .map((flavor) => ({
            id: flavor.flavor_id,
            name: `${flavor.flavor_vpcu} vCPU${flavor.flavor_vpcu > 1 ? "s" : ""} / ${
              flavor.flavor_ram
            }GB RAM / ${flavor.flavor_storage}GB Storage`,
            vcpus: flavor.flavor_vpcu,
            ram: flavor.flavor_ram,
            disk: flavor.flavor_storage,
            basePrice: Math.round(flavor.naira_price * 0.85),
            partnerPrice: flavor.naira_price,
            dollarPrice: flavor.dollar_price,
            snapshotNairaPrice: flavor.snapshot_naira_price,
            snapshotDollarPrice: flavor.snapshot_dollar_price,
            egressNairaPrice: flavor.egress_naira_price,
            egressDollarPrice: flavor.egress_dollar_price,
          }))
          .sort((a, b) => a.partnerPrice - b.partnerPrice);

        // Map resources
        const mappedResources = [
          {
            id: 1,
            name: "Volume Storage (per GB)",
            basePrice: Math.round(pricingData.volume_naira_price * 0.85),
            partnerPrice: pricingData.volume_naira_price,
            type: "storage",
            dollarPrice: pricingData.volume_dollar_price,
          },
          {
            id: 2,
            name: "Volume Backup (per GB)",
            basePrice: Math.round(pricingData.volume_backup_naira_price * 0.85),
            partnerPrice: pricingData.volume_backup_naira_price,
            type: "storage",
            dollarPrice: pricingData.volume_backup_dollar_price,
          },
          {
            id: 3,
            name: "Volume Snapshot (per GB)",
            basePrice: Math.round(pricingData.volume_snapshot_naira_price * 0.85),
            partnerPrice: pricingData.volume_snapshot_naira_price,
            type: "storage",
            dollarPrice: pricingData.volume_snapshot_dollar_price,
          },
          {
            id: 4,
            name: "Floating IP Address",
            basePrice: Math.round(pricingData.floating_ip_naira_price * 0.85),
            partnerPrice: pricingData.floating_ip_naira_price,
            type: "compute",
            dollarPrice: pricingData.floating_ip_dollar_price,
          },
          {
            id: 5,
            name: "Additional IP Address",
            basePrice: Math.round(pricingData.additional_ip_addresses_naira_price * 0.85),
            partnerPrice: pricingData.additional_ip_addresses_naira_price,
            type: "compute",
            dollarPrice: pricingData.additional_ip_addresses_dollar_price,
          },
          {
            id: 6,
            name: "Network Interface",
            basePrice: Math.round(pricingData.interface_naira_price * 0.85),
            partnerPrice: pricingData.interface_naira_price,
            type: "compute",
            dollarPrice: pricingData.interface_dollar_price,
          },
          {
            id: 7,
            name: "Load Balancer Pool Member",
            basePrice: Math.round(pricingData.pool_member_price_naira * 0.85),
            partnerPrice: pricingData.pool_member_price_naira,
            type: "compute",
            dollarPrice: pricingData.pool_member_price_dollar,
          },
        ].filter(Boolean);

        const totalResourceCount = allResources.length;

        setMockData({
          resources: mappedResources,
          flavors: mappedFlavors,
          wallet: {
            balance: partnerWallet.wallet.commission || 0,
            currency: "NGN",
            lastUpdated: new Date().toISOString().split("T")[0],
          },
          analytics: {
            totalRevenue: partnerWallet.wallet.commission || 0,
            activeCustomers: totalResourceCount > 0 ? Math.ceil(totalResourceCount / 3) : 0,
            activeResources: totalResourceCount,
          },
        });
      } catch (error) {
        console.error("Error processing partner data:", error);
      }
    }
  }, [
    partnerFlavors,
    partnerPricing,
    partnerWallet,
    partnerResourcesServer,
    partnerResourcesVolume,
    partnerResourcesCluster,
    partnerResourcesFloatingIp,
    partnerResourcesInterface,
    partnerResourcesPoolMember,
    partnerResourcesBackup,
    partnerResourcesServerSnapshot,
    partnerResourcesVolumeSnapshot,
  ]);

  // Formik forms
  const priceUpdateForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      resourceId: editingResource?.id || "",
      newPrice: editingResource?.partnerPrice || "",
    },
    validationSchema: PRICE_VALIDATION_SCHEMA,
    onSubmit: async (values, { resetForm }) => {
      try {
        setMockData((prev) => ({
          ...prev,
          resources: prev.resources.map((r) =>
            r.id === values.resourceId ? { ...r, partnerPrice: Number(values.newPrice) } : r
          ),
        }));
        setEditingResource(null);
        resetForm();
      } catch (error) {
        console.error("Error updating resource price:", error);
      }
    },
  });

  const flavorPriceUpdateForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      flavorId: editingFlavor?.id || "",
      newPrice: editingFlavor?.partnerPrice || "",
    },
    validationSchema: PRICE_VALIDATION_SCHEMA,
    onSubmit: async (values, { resetForm }) => {
      try {
        setMockData((prev) => ({
          ...prev,
          flavors: prev.flavors.map((f) =>
            f.id === values.flavorId ? { ...f, partnerPrice: Number(values.newPrice) } : f
          ),
        }));

        console.log(values)

        const flavorData = {
          naira_price: values?.newPrice,
        }

        const { payload } = await dispatch(setPartnerFlavorPrices({
          flavorData,
          flavorId: values?.flavorId,
        }));

        console.log(payload);

        if(payload && payload?.statuscode === "00") {
          setEditingFlavor(null);
          resetForm();
          window.location.reload()
        }

      } catch (error) {
        console.error("Error updating flavor price:", error);
      }
    },
  });

  // Component: StatCard
  const StatCard = ({ title, value, icon: Icon, color, change }) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all duration-200">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          {change && (
            <div className="flex items-center mt-2">
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
              <span className="text-sm text-green-600 dark:text-green-400">{change}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  // Render Dashboard Tab
  const renderDashboard = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Welcome back, {partnerInfo?.name || "Partner"}
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
            {currentSubdomain || "cloudvantage"}.cloudvantage.com.ng
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Revenue"
          value={`₦${mockData.analytics.totalRevenue.toLocaleString()}`}
          icon={DollarSign}
          color="bg-[#1da1fc]"
          change="+12.5%"
        />
        <StatCard
          title="Active Customers"
          value={mockData.analytics.activeCustomers}
          icon={Users}
          color="bg-green-500"
          change="+5.2%"
        />
        <StatCard
          title="Active Resources"
          value={mockData.analytics.activeResources}
          icon={Server}
          color="bg-purple-500"
          change="+8.1%"
        />
        <StatCard
          title="Wallet Balance"
          value={`₦${mockData.wallet.balance.toLocaleString()}`}
          icon={Wallet}
          color="bg-orange-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {[
              { action: "New customer registration", time: "2 hours ago", type: "user" },
              { action: "VPS instance created", time: "4 hours ago", type: "server" },
              { action: "Payment received", time: "6 hours ago", type: "payment" },
            ].map((activity, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    activity.type === "user"
                      ? "bg-blue-100 dark:bg-blue-900/30"
                      : activity.type === "server"
                      ? "bg-green-100 dark:bg-green-900/30"
                      : "bg-yellow-100 dark:bg-yellow-900/30"
                  }`}
                >
                  <Activity className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{activity.action}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Revenue Breakdown</h3>
          <div className="space-y-4">
            {[
              {
                name: "Compute Services",
                amount: Math.round(mockData.analytics.totalRevenue * 0.71),
                percentage: 71,
                color: "bg-[#1da1fc]",
              },
              {
                name: "Storage Services",
                amount: Math.round(mockData.analytics.totalRevenue * 0.20),
                percentage: 20,
                color: "bg-green-500",
              },
              {
                name: "Network Services",
                amount: Math.round(mockData.analytics.totalRevenue * 0.09),
                percentage: 9,
                color: "bg-purple-500",
              },
            ].map((item, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.name}</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    ₦{item.amount.toLocaleString()} ({item.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className={`${item.color} h-2 rounded-full`} style={{ width: `${item.percentage}%` }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Render Resources Tab
  const renderResourcesTab = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Resources</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your partner resources and pricing</p>
        </div>
        <span className="mt-4 sm:mt-0 text-sm text-gray-600 dark:text-gray-400">
          Total Active: {mockData.analytics.activeResources}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Compute Resources"
          value={mockData.resources.filter((r) => r.type === "compute").length}
          icon={Server}
          color="bg-[#1da1fc]"
        />
        <StatCard
          title="Storage Resources"
          value={mockData.resources.filter((r) => r.type === "storage").length}
          icon={Database}
          color="bg-green-500"
        />
        <StatCard
          title="Total Revenue"
          value={`₦${mockData.analytics.totalRevenue.toLocaleString()}`}
          icon={BarChart3}
          color="bg-purple-500"
        />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Resource Pricing</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Resource Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Base Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Your Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Margin
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {mockData.resources.map((resource) => {
                const margin = ((resource.partnerPrice - resource.basePrice) / resource.basePrice * 100).toFixed(1);

                return (
                  <tr key={resource.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{resource.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          resource.type === "compute"
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                            : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                        }`}
                      >
                        {resource.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      ₦{resource.basePrice.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      ₦{resource.partnerPrice.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`font-semibold ${
                          margin >= 20
                            ? "text-green-600 dark:text-green-400"
                            : margin >= 10
                            ? "text-yellow-600 dark:text-yellow-400"
                            : "text-red-600 dark:text-red-400"
                        }`}
                      >
                        {margin}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => setEditingResource(resource)}
                        className="inline-flex items-center space-x-1 text-[#1da1fc] hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                      >
                        <Edit2 className="h-4 w-4" />
                        <span>Edit</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Render Flavor Pricing Tab
  const renderFlavorPricingTab = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">VPS Flavor Pricing</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Set custom prices for VPS configurations</p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">VPS Flavors</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Flavor Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Specifications
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Base Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Your Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Margin
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {mockData.flavors.map((flavor) => {
                const margin = ((flavor.partnerPrice - flavor.basePrice) / flavor.basePrice * 100).toFixed(1);

                return (
                  <tr key={flavor.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{flavor.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded">
                          {flavor.vcpus} vCPUs
                        </span>
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded">
                          {flavor.ram}GB RAM
                        </span>
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded">
                          {flavor.disk}GB Disk
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      ₦{flavor.basePrice.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      ₦{flavor.partnerPrice.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`font-semibold ${
                          margin >= 20
                            ? "text-green-600 dark:text-green-400"
                            : margin >= 10
                            ? "text-yellow-600 dark:text-yellow-400"
                            : "text-red-600 dark:text-red-400"
                        }`}
                      >
                        {margin}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => setEditingFlavor(flavor)}
                        className="inline-flex items-center space-x-1 text-[#1da1fc] hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                      >
                        <Edit2 className="h-4 w-4" />
                        <span>Edit</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Render Wallet Tab
  const renderWalletTab = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Partner Wallet</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your wallet and finances</p>
        </div>
        <span className="mt-4 sm:mt-0 text-sm text-gray-600 dark:text-gray-400">
          Last updated: {mockData.wallet.lastUpdated}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Current Balance"
          value={`₦${mockData.wallet.balance.toLocaleString()}`}
          icon={Wallet}
          color="bg-green-500"
        />
        <StatCard
          title="Total Earned"
          value={`₦${mockData.analytics.totalRevenue.toLocaleString()}`}
          icon={DollarSign}
          color="bg-[#1da1fc]"
        />
        <StatCard
          title="Active Customers"
          value={mockData.analytics.activeCustomers}
          icon={Users}
          color="bg-purple-500"
        />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Wallet Management</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:border-[#1da1fc] dark:hover:border-[#1da1fc] transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Minimum Balance Alert</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Get notified when balance falls below threshold
                </p>
              </div>
              <button className="px-4 py-2 bg-[#1da1fc] text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium">
                Configure
              </button>
            </div>
          </div>

          <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:border-green-500 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Auto Top-up</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Automatically add funds when balance is low
                </p>
              </div>
              <button className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium">
                Enable
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Analytics Tab
  const renderAnalyticsTab = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics & Reports</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Track your performance and growth</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Revenue"
          value={`₦${mockData.analytics.totalRevenue.toLocaleString()}`}
          icon={DollarSign}
          color="bg-green-500"
          change="+12.5%"
        />
        <StatCard
          title="Active Customers"
          value={mockData.analytics.activeCustomers}
          icon={Users}
          color="bg-[#1da1fc]"
          change="+5.2%"
        />
        <StatCard
          title="Active Resources"
          value={mockData.analytics.activeResources}
          icon={Server}
          color="bg-purple-500"
          change="+8.1%"
        />
        <StatCard
          title="Avg Margin"
          value="17.6%"
          icon={BarChart3}
          color="bg-orange-500"
          change="+2.3%"
        />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Revenue Breakdown</h3>
        <div className="space-y-4">
          {[
            {
              name: "Compute Services",
              amount: Math.round(mockData.analytics.totalRevenue * 0.71),
              percentage: 71,
              color: "bg-[#1da1fc]",
            },
            {
              name: "Storage Services",
              amount: Math.round(mockData.analytics.totalRevenue * 0.20),
              percentage: 20,
              color: "bg-green-500",
            },
            {
              name: "Network Services",
              amount: Math.round(mockData.analytics.totalRevenue * 0.09),
              percentage: 9,
              color: "bg-purple-500",
            },
          ].map((item, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className={`w-4 h-4 rounded-full ${item.color}`}></div>
                <span className="font-medium text-gray-900 dark:text-white">{item.name}</span>
              </div>
              <div className="text-right">
                <div className="font-bold text-gray-900 dark:text-white">
                  ₦{item.amount.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{item.percentage}% of total</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Render Settings Tab
  const renderSettingsTab = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">White-label Settings</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Configure your brand and partner settings</p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Branding Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Domain Name</label>
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
              <p className="text-sm text-gray-900 dark:text-gray-100">
                {currentSubdomain ? `${currentSubdomain}.cloudvantage.com.ng` : "Not set"}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Partner Name</label>
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
              <p className="text-sm text-gray-900 dark:text-gray-100">{partnerInfo?.name || "Not set"}</p>
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
              <p className="text-sm text-gray-900 dark:text-gray-100">{partnerInfo?.description || "Not set"}</p>
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Logo URL</label>
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
              <p className="text-sm text-gray-900 dark:text-gray-100">
                {partnerInfo?.logo || "Using default CloudVantage logo"}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <button className="px-6 py-3 bg-[#1da1fc] text-white rounded-lg hover:bg-blue-600 transition-colors font-medium">
            Update Branding
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Partner Configuration</h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white">Custom Domain</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Use your own domain instead of subdomain
              </p>
            </div>
            <button className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors font-medium">
              Configure
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white">White-label Status</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Enable/disable white-label features</p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-green-600 dark:text-green-400">Active</span>
              <div className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-green-600 transition-colors duration-200 ease-in-out">
                <span className="sr-only">Use setting</span>
                <span className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out translate-x-5"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case "dashboard":
        return renderDashboard();
      case "resources":
        return renderResourcesTab();
      case "pricing":
        return renderFlavorPricingTab();
      case "wallet":
        return renderWalletTab();
      case "analytics":
        return renderAnalyticsTab();
      case "settings":
        return renderSettingsTab();
      default:
        return renderDashboard();
    }
  };

  // Early return for non-partner mode
  if (!shouldLoadPartnerData && !partnerLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Partner Access Required</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You need to be in partner mode to access this admin panel.
          </p>
          <Link
            to="/client-area"
            className="inline-flex items-center px-4 py-2 bg-[#1da1fc] text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
      <Spinner loading={partnerLoading || userLoading} />

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#1da1fc] shadow-lg transform ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-blue-400/30">
          <Link to="/client-area" className="flex items-center space-x-2">
            <img src={WhiteLogoIcon} alt="CloudVantage" className="h-6 hidden sm:block" />
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-blue-200 hover:text-white hover:bg-blue-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-2.5 py-4">
          <div className="text-xs font-medium text-blue-200 uppercase tracking-wider mb-3">Partner Dashboard</div>
          {partnerInfo && (
            <div className="mb-4 p-3 bg-[#39a7f1] rounded-lg border border-gray-300">
              <div className="text-sm font-medium text-white">{capitalizeFirstLetter(partnerInfo?.name)}</div>
              <div className="text-xs text-blue-200">{currentSubdomain}.cloudvantage.com.ng</div>
            </div>
          )}
        </div>

        <nav className="px-3 pb-4">
          <ul className="space-y-1">
            {NAVIGATION_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => {
                      setActiveTab(item.id);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center px-3 py-4 text-sm font-medium rounded-lg transition-colors ${
                      activeTab === item.id
                        ? "bg-white text-[#1da1fc] shadow-sm"
                        : "text-blue-100 hover:bg-white hover:text-primaryBlue"
                    }`}
                  >
                    <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    {item.label}
                    {activeTab === item.id && <ChevronRight className="ml-auto h-4 w-4" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-blue-400/30">
          <button
            onClick={switchToCustomerView}
            className="w-full flex items-center justify-center px-4 py-3 text-sm font-medium text-white border border-white hover:bg-[#0b94f0] rounded-lg transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Customers View
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 lg:ml-0">
        {/* Top Header */}
        <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-2 rounded-md text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  <Menu className="h-5 w-5" />
                </button>
                <div className="lg:hidden ml-2">
                  <h1 className="text-lg font-semibold text-gray-900 dark:text-white capitalize">{activeTab}</h1>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <Link
                  to="/sign-out"
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                >
                  Sign Out
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="px-4 sm:px-6 lg:px-8 py-8">{renderContent()}</div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Resource Price Edit Modal */}
      {editingResource && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Edit Price for {editingResource.name}
              </h3>
              <button
                onClick={() => setEditingResource(null)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={priceUpdateForm.handleSubmit} className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Base Price: <span className="font-semibold text-gray-900 dark:text-white">₦{editingResource.basePrice.toLocaleString()}</span>
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Current Margin:{" "}
                  <span className="font-semibold text-[#1da1fc]">
                    {(((editingResource.partnerPrice - editingResource.basePrice) / editingResource.basePrice) * 100).toFixed(1)}%
                  </span>
                </p>
              </div>

              <Input
                label="Your Price (₦)"
                type="number"
                name="newPrice"
                value={priceUpdateForm.values.newPrice}
                onChange={priceUpdateForm.handleChange}
                onBlur={priceUpdateForm.handleBlur}
                onError={
                  priceUpdateForm.errors.newPrice && priceUpdateForm.touched.newPrice
                    ? priceUpdateForm.errors.newPrice
                    : null
                }
              />

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingResource(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-[#1da1fc] rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <Check className="h-4 w-4" />
                  <span>Update Price</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Flavor Price Edit Modal */}
      {editingFlavor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Edit Price for {editingFlavor.name}
              </h3>
              <button
                onClick={() => setEditingFlavor(null)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={flavorPriceUpdateForm.handleSubmit} className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Base Price: <span className="font-semibold text-gray-900 dark:text-white">₦{editingFlavor.basePrice.toLocaleString()}</span>
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Current Margin:{" "}
                  <span className="font-semibold text-[#1da1fc]">
                    {(((editingFlavor.partnerPrice - editingFlavor.basePrice) / editingFlavor.basePrice) * 100).toFixed(1)}%
                  </span>
                </p>
              </div>

              <Input
                label="Your Price (₦)"
                type="number"
                name="newPrice"
                value={flavorPriceUpdateForm.values.newPrice}
                onChange={flavorPriceUpdateForm.handleChange}
                onBlur={flavorPriceUpdateForm.handleBlur}
                onError={
                  flavorPriceUpdateForm.errors.newPrice && flavorPriceUpdateForm.touched.newPrice
                    ? flavorPriceUpdateForm.errors.newPrice
                    : null
                }
              />

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingFlavor(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-[#1da1fc] rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <Check className="h-4 w-4" />
                  <span>Update Price</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerAdminLayout;